import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { connect } from 'react-redux'
import { buildSuggestedDatesParamsKey, buildSuggestedDatesString } from 'lib/search/searchUtils'
import { getSuggestedDates } from 'selectors/offerSelectors'
import { prettyDates } from 'components/Search/utils'
import moment from 'moment'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import { useOfferPrice } from 'hooks/Offers/useOfferPrice'
import useOfferTilePriceBlockExperiment from 'hooks/Experiments/useOfferTilePriceBlockExperiment'
import OfferTilePriceBlockCaption from 'components/OfferList/OfferListTiles/Support/OfferTilePriceBlockCaption'
import { isDisplayablePrice } from 'lib/offer/pricingVisibility'

interface MappedStateProps {
  suggestedDates?: App.OfferSuggestedDates
}
export interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  price: number
  duration: number
  filters: App.OfferListFilters
  hotelOnlyPrice?: boolean
  pricedUnit?: string
}

function MapViewBedbankPricing(props: Props & MappedStateProps) {
  const {
    offer,
    price,
    duration,
    suggestedDates,
    filters,
    hotelOnlyPrice,
    pricedUnit,
  } = props

  const intl = useIntl()
  const hasSuggestedCheckInCheckOut = suggestedDates?.checkIn && suggestedDates?.checkOut

  const hasDates = !!filters?.checkIn

  const fromLabel = intl.formatMessage({
    id: '3L3jbCIEiahyALfekaL0',
    defaultMessage: 'from',
  })

  const durationText = useMemo(() => {
    if (hasDates) {
      return prettyDates(intl, moment(filters?.checkIn), moment(filters?.checkOut))
    }
    if (!duration) {
      return fromLabel
    }
    if (hasSuggestedCheckInCheckOut) {
      return intl.formatMessage(
        {
          id: '4iHhPuJxvfE2cDBevs00',
          defaultMessage: '{dates} from',
        },
        { dates: buildSuggestedDatesString(suggestedDates.checkIn, suggestedDates.checkOut) },
      )
    }
    return intl.formatMessage(
      {
        id: 'rRBhNThtFcLKdmAVzaC4',
        defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
      },
      { nights: duration },
    )
  }, [duration, filters?.checkIn, filters?.checkOut, hasDates, hasSuggestedCheckInCheckOut, suggestedDates?.checkIn, suggestedDates?.checkOut, fromLabel, intl])

  const {
    saleUnit,
    total,
  } = useOfferPrice({
    value: undefined,
    saleUnit: offer.saleUnit,
    duration,
    total: suggestedDates?.price ?? price,
  })
  const priceBlockVariation = useOfferTilePriceBlockExperiment(offer, hotelOnlyPrice,
    isDisplayablePrice(total) && !filters.viewPriceInPoints)
  const usesSuggestedPrice = suggestedDates?.price != null
  const isPerPersonPrice = !usesSuggestedPrice && pricedUnit === 'person'

  return <div>
    <OfferTilePriceBlockCaption
      priceBlockExperimentVariation={priceBlockVariation}
      duration={duration}
      checkIn={filters.checkIn ?? suggestedDates?.checkIn}
      checkOut={filters.checkOut ?? suggestedDates?.checkOut}
      saleUnit={isPerPersonPrice ? 'person' : offer.saleUnit}
      unitCount={isPerPersonPrice ? 1 : filters.rooms?.length}
      isPricePerNight={!usesSuggestedPrice && pricedUnit === 'night'}
    >{durationText}
    </OfferTilePriceBlockCaption>
    <PriceRowPrice
      size="S"
      price={total}
      saleUnit={priceBlockVariation ? 'total' : saleUnit}
    />
  </div>
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(ownProps.filters?.flexibleMonths, ownProps.filters?.flexibleNights, ownProps.filters?.rooms)
  return {
    suggestedDates: getSuggestedDates(appState, flexibleSearchFilterKey, ownProps.offer.id),
  }
})(MapViewBedbankPricing)
